'use client';

import { useEffect, useState } from 'react';
import { useCurrentUser } from '@/hooks/use-current-user';

interface AccessControlProps {
    pageStatus: 'draft' | 'published';
    pageCreatedBy: string;
    children: React.ReactNode;
}

export default function AccessControl({ pageStatus, pageCreatedBy, children }: AccessControlProps) {
    const [currentUser, authStatus] = useCurrentUser();
    const [checkingAccess, setCheckingAccess] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);

    useEffect(() => {
        // Published pages are accessible to everyone
        if (pageStatus === 'published') {
            setHasAccess(true);
            setCheckingAccess(false);
            return;
        }

        // Draft pages require authentication
        if (authStatus === 'pending') {
            // Still loading auth state
            return;
        }

        if (authStatus === 'logged-out' || !currentUser) {
            console.log('[AccessControl] User not authenticated');
            setHasAccess(false);
            setCheckingAccess(false);
            return;
        }

        // Check if current user is the creator
        const userEmail = currentUser.email;
        console.log('[AccessControl] User email:', userEmail);
        console.log('[AccessControl] Page creator:', pageCreatedBy);

        if (userEmail === pageCreatedBy) {
            console.log('[AccessControl] Access granted');
            setHasAccess(true);
        } else {
            console.log('[AccessControl] Access denied');
            setHasAccess(false);
        }

        setCheckingAccess(false);
    }, [currentUser, authStatus, pageStatus, pageCreatedBy]);

    // Show loading state while checking access
    if (checkingAccess) {
        return (
            <div style={{
                paddingTop: 'var(--total-header-height)',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                transition: 'padding-top 300ms ease-in-out',
            }}>
                <div style={{
                    textAlign: 'center',
                    color: 'white'
                }}>
                    <div style={{
                        width: '60px',
                        height: '60px',
                        margin: '0 auto 1.5rem',
                        border: '4px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '4px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <p style={{ fontSize: '1.2rem', fontWeight: '600' }}>
                        Verifying access...
                    </p>
                    <style dangerouslySetInnerHTML={{
                        __html: `
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `
                    }} />
                </div>
            </div>
        );
    }

    // Show access denied page
    if (!hasAccess) {
        return (
            <div style={{
                paddingTop: 'var(--total-header-height)',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                transition: 'padding-top 300ms ease-in-out',
            }}>
                <div style={{
                    background: 'white',
                    borderRadius: '20px',
                    padding: '3rem 2.5rem',
                    maxWidth: '500px',
                    textAlign: 'center',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    animation: 'fadeInScale 0.5s ease-out'
                }}>
                    <div style={{
                        width: '80px',
                        height: '80px',
                        margin: '0 auto 1.5rem',
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '2.5rem',
                    }}>
                        🔒
                    </div>
                    <h2 style={{
                        margin: '0 0 1rem 0',
                        fontSize: '2rem',
                        fontWeight: '700',
                        color: '#2d3748',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>
                        Access Denied
                    </h2>
                    <p style={{
                        margin: '0 0 1.5rem 0',
                        fontSize: '1.1rem',
                        color: '#4a5568',
                        lineHeight: '1.6'
                    }}>
                        This page is currently in <strong style={{ color: '#667eea' }}>draft mode</strong> and is only visible to its creator.
                    </p>
                    <p style={{
                        margin: '0 0 2rem 0',
                        fontSize: '0.95rem',
                        color: '#718096',
                        lineHeight: '1.5'
                    }}>
                        If you believe you should have access to this page, please contact the page owner or administrator.
                    </p>
                    <a
                        href="/"
                        className="access-denied-home-btn"
                    >
                        🏠 Go to Home
                    </a>
                </div>
                <style dangerouslySetInnerHTML={{
                    __html: `
                        @keyframes fadeInScale {
                            from {
                                opacity: 0;
                                transform: scale(0.9);
                            }
                            to {
                                opacity: 1;
                                transform: scale(1);
                            }
                        }
                        .access-denied-home-btn {
                            display: inline-block;
                            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                            color: white;
                            padding: 0.875rem 2rem;
                            border-radius: 50px;
                            text-decoration: none;
                            font-size: 1rem;
                            font-weight: 600;
                            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
                            transition: transform 0.2s, box-shadow 0.2s;
                            cursor: pointer;
                        }
                        .access-denied-home-btn:hover {
                            transform: translateY(-2px);
                            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
                        }
                    `
                }} />
            </div>
        );
    }

    // Access granted - render the page content
    return <>{children}</>;
}
